import {
  ChangeEvent,
  Fragment,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Link,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { ContentFlag } from "~/components/content-flag";
import { ClickableTableRow } from "~/components/report";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { FormatRelativeDateAbbreviated } from "~/lib/date";
import { useCanDelete } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Delete";
import {
  ClientIntegration,
  GetIntegrationsSummaryDocument,
  IntegrationType,
  ListClientIntegrationsDocument,
  useDeleteClientIntegrationMutation,
  useListClientIntegrationsQuery,
} from "~/operations";

import { pluralize } from "~/lib/pluralize";
import { IntegrationsListHeader } from "../../headers/IntegrationsListHeader";
import { HostedIntegrationsTableEmptyRow } from "./HostedIntegrationsTableEmptyRow";
import {
  mapIntegrationRouteToClientIntegrationType,
  mapIntegrationRouteToIntegrationType,
  mapIntegrationRouteToTableHeaders,
  mapIntegrationRouteToTitle,
} from "../utils";
import {
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { DataTable } from "~/components/data-table";
import { setDocumentTitle } from "~/lib/commonUtils";
import { ConfirmationDialog } from "~/components/ConfirmationDialog";
import { ChatOpsIntegrations } from "../../chatOps/ChatOpsIntegrations";
import { ChatIntegrationName } from "../../chatOps/utils/chatOpsTypes";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { OrganizationScope, ScopeType, SpaceScope } from "~/hooks/useScope";
import {
  getIntegrationsListHref,
  integrationDetailHref,
} from "~/pages/integrations/utils/integrationLinksUtils";

export interface HostedIntegrationListPageProps {
  scope: SpaceScope | OrganizationScope;
}

export function HostedIntegrationListPage({
  scope,
}: HostedIntegrationListPageProps) {
  const navigate = useNavigate();
  const { integrationTypeId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, _setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selection, setSelection] = useState<ClientIntegration["mrn"][]>([]);

  const integrationTitle = mapIntegrationRouteToTitle(integrationTypeId);
  const clientIntegrationTypes =
    mapIntegrationRouteToClientIntegrationType(integrationTypeId);
  const integrationType =
    mapIntegrationRouteToIntegrationType(integrationTypeId);
  const tableHeaders = mapIntegrationRouteToTableHeaders(integrationTypeId);
  const triageToolbar = useTriageToolbar();

  useEffect(() => {
    if (selection.length > 0) {
      triageToolbar.show({
        buttons: [
          {
            type: TriageToolbarButtonType.Delete,
            onClick: handleDeleteClick,
          },
        ],
        payload: {
          count: selection.length,
          totalCount: integrations.length,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selection]);

  const { data, loading, error } = useListClientIntegrationsQuery({
    variables: {
      input: {
        spaceMrn: scope.mrn,
        filterQuery: {
          types: clientIntegrationTypes,
        },
      },
    },
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
    pollInterval: 10000,
  });

  const [deleteIntegration] = useDeleteClientIntegrationMutation({
    refetchQueries: [
      ListClientIntegrationsDocument,
      GetIntegrationsSummaryDocument,
    ],
  });

  // Check if user has permission to delete integrations
  // AWS integrations cannot be deleted via multi-select
  const hasIntegrationDeletePermission = useCanDelete();
  const isSelectable =
    hasIntegrationDeletePermission && integrationType !== IntegrationType.Aws;

  setDocumentTitle([integrationTitle, "Integrations", scope.name]);

  if (error) {
    return <LoadingFailedPage what="integrations" />;
  }

  if (loading || !data?.listClientIntegrations) {
    return <LoadingPage what="integrations" />;
  }

  const { integrations } = data.listClientIntegrations;
  const hasIntegrations = Boolean(integrations?.length > 0);

  const getId = (mrn: string) => {
    return [...mrn.split(`/`)].pop();
  };

  const handleSelection = (
    e: ChangeEvent<HTMLInputElement>,
    mrn: ClientIntegration["mrn"],
  ) => {
    if (e.target.checked) {
      setSelection((prev) => [...prev, mrn]);
    } else {
      const updated = selection.filter((x) => x !== mrn);
      setSelection(updated);
    }
  };

  const handleSelectAll = () => {
    const updated =
      selection.length > 0 && integrations.length > 0
        ? []
        : integrations.flatMap((x) => (x?.mrn ? [x.mrn] : []));
    setSelection(updated);
  };

  const handleDeleteClick = () => {
    handleDelete();
  };

  const handleCancelClick = () => {
    setSelection([]);
  };

  //Delete functionality
  const handleDelete = async () => {
    try {
      Promise.all(
        selection.map((mrn) =>
          deleteIntegration({ variables: { input: { mrn: mrn } } }),
        ),
      );
      enqueueSnackbar(
        `Successfully removed ${
          selection.length
        } ${integrationTitle} ${pluralize(
          "integration",
          selection.length,
        )} from Mondoo`,
        {
          variant: "success",
        },
      );
      setSelection([]);
    } catch {
      enqueueSnackbar(
        `Could not remove ${selection.length} ${integrationTitle} ${pluralize(
          "integration",
          selection.length,
        )} from Mondoo`,
        {
          variant: "error",
        },
      );
    } finally {
      setIsOpen(false);
    }
  };

  const routeToServiceAccounts = (e: SyntheticEvent, mrn: string) => {
    e.stopPropagation();
    searchParams.set("queryTerms", mrn);
    navigate(`/space/settings/serviceaccounts?${searchParams}`);
  };

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/${scope.type === ScopeType.Space ? "space" : "organization"}/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography
      key={1}
      component={RouterLink}
      color="primary.light"
      to={getIntegrationsListHref({ scope })}
    >
      Integrations
    </Typography>,
    <Typography key={2}>{integrationTitle}</Typography>,
  ];

  // Show chatops view if integration type is one of chatops
  if (
    integrationTypeId &&
    ["msteams", "slack", "telegram", "httppost"].includes(integrationTypeId)
  ) {
    return (
      <Fragment>
        <Breadcrumbs sx={{ mb: 4 }} separator="›">
          {breadcrumbs}
        </Breadcrumbs>
        <ChatOpsIntegrations
          {...{
            scope,
            view: integrationTypeId as ChatIntegrationName,
          }}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs sx={{ mb: 4 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      <IntegrationsListHeader
        {...{
          scope,
          type: integrationType,
          hasIntegrations,
        }}
      />
      <Box sx={{ mb: 5 }} />
      <DataTable
        id={`${integrationTypeId}-integrations-table`}
        selectable={isSelectable}
        selection={selection}
      >
        {hasIntegrations && (
          <TableHead>
            <TableRow>
              {isSelectable && (
                <TableCell>
                  <Checkbox
                    checked={
                      integrations.length > 0 &&
                      selection.length === integrations.length
                    }
                    indeterminate={
                      selection.length > 0 &&
                      selection.length < integrations.length
                    }
                    onChange={handleSelectAll}
                    disabled={integrations.length === 0}
                  />
                </TableCell>
              )}
              {tableHeaders.map((header) => {
                return (
                  <TableCell
                    key={header.id}
                    align={header.options?.textAlign}
                    width={header.options?.width}
                  >
                    <TableSortLabel
                      direction="desc"
                      active={header.id === "CREATED_AT"}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {hasIntegrations ? (
            integrations
              .flatMap((integration) => integration ?? [])
              .map((integration) => {
                const { type, mrn } = integration;
                const id = getId(mrn);
                const href = integrationDetailHref(type, mrn, scope);
                const tableRowsMaps =
                  mapIntegrationRouteToTableHeaders(integrationTypeId);
                const integrationStatus =
                  integration?.status.toLowerCase() === "not_ready"
                    ? "pending"
                    : integration?.status.split("_").join(" ");

                const isSelected = selection.includes(integration.mrn);
                const className = isSelected ? "selected" : "";

                return (
                  <ClickableTableRow
                    key={id}
                    onClick={() => navigate(href)}
                    className={className}
                    data-name={`${integrationTypeId}-${getId(integration.mrn)}`}
                  >
                    {isSelectable && (
                      <TableCell>
                        <Checkbox
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleSelection(e, integration.mrn)}
                          checked={isSelected}
                        />
                      </TableCell>
                    )}
                    {tableRowsMaps.map((header) => (
                      <TableCell
                        key={header.id}
                        align={header.options?.textAlign}
                        width={header.options?.width}
                      >
                        {header.id === "NAME" ? (
                          integration.name
                        ) : header.id === "STATUS" ? (
                          <ContentFlag
                            flag={integrationStatus}
                            color={integration.status}
                          />
                        ) : header.id === "CREATED_AT" ? (
                          FormatRelativeDateAbbreviated(integration.created)
                        ) : header.id === "LAST_SCAN" ||
                          header.id === "LAST_EXPORT" ||
                          header.id === "LAST_UPDATED" ? (
                          FormatRelativeDateAbbreviated(integration.lastCheckin)
                        ) : header.id === "SERVICE_ACCOUNT" &&
                          integrationStatus !== "pending" ? (
                          <Button
                            variant="text"
                            color="secondary"
                            onClick={(e) =>
                              routeToServiceAccounts(e, integration.mrn)
                            }
                            endIcon={<ArrowForwardIcon />}
                          >
                            Service Account
                          </Button>
                        ) : header.id === "GITHUB_TYPE" &&
                          integration.configurationOptions?.__typename ===
                            "GithubConfigurationOptions" ? (
                          integration.configurationOptions.repository ? (
                            "Repository"
                          ) : (
                            "Organization"
                          )
                        ) : header.id === "K8S_VERSION" ? (
                          integration.lastStateInfo?.k8sVersion || "unknown"
                        ) : header.id === "TYPE" ? (
                          integration.configurationOptions?.__typename ===
                          "AWSConfigurationOptions" ? (
                            `Serverless, ${
                              integration.configurationOptions.isOrganization
                                ? "organization"
                                : "single account"
                            }`
                          ) : integration.configurationOptions?.__typename ===
                            "HostedAwsConfigurationOptions" ? (
                            "Hosted, single account"
                          ) : (
                            ""
                          )
                        ) : null}
                      </TableCell>
                    ))}
                  </ClickableTableRow>
                );
              })
          ) : (
            <HostedIntegrationsTableEmptyRow
              scope={scope}
              integrationTypeId={integrationTypeId}
            />
          )}
        </TableBody>
      </DataTable>
      {/* Delete Integration Modal */}
      <ConfirmationDialog
        isOpen={isOpen}
        confirmButtonText="DELETE"
        title={`Remove ${integrationTitle} Integration`}
        content={
          <Typography>
            Are you absolutely sure you want to remove this integration? This
            operation cannot be undone.
          </Typography>
        }
        onConfirm={handleDelete}
        onClose={() => setIsOpen(false)}
        loading={loading}
      />
    </Fragment>
  );
}
